import {
  FREE_QUOTA_SUBMIT_WEB_HOOK_URL,
  SUBMIT_WEB_HOOK_URL,
} from '@/consts/urls';
import { getContext } from '@/context';
import getAuthJsonHeaders from '@/requests/common/getAuthJsonHeaders';

import type { ContentFileAttributes, NotificationConfig } from '@/types';

const QUOTA_EXCEEDED_STATUS = 429;

/**
 * Why a run is claiming a free generation. Sent on every guest-endpoint submit
 * as `x-free-quota-reason` — the endpoint serves more than one free case now,
 * and the backend meters them separately, so the reason travels with the run
 * instead of being inferred from the token.
 */
export type FreeQuotaReason = 'onboarding' | 'free';

const FREE_QUOTA_REASON_HEADER = 'x-free-quota-reason';

/**
 * The free-quota webhook is out of free runs for this user. Distinct from a
 * transport rate limit: nobody waits, the generation just costs credits now.
 */
export class FreeQuotaExceededError extends Error {
  constructor() {
    super('Free generation quota exceeded');
    this.name = 'FreeQuotaExceededError';
  }
}

type SubmitWebhookData = {
  response: { id: string };
  status?: string;
  reason?: string;
};

type DriveOptions = {
  folderName: string;
  packageId?: string;
  // See `ContentFileAttributes` / docs/reference/api.md → "Drive attributes workaround".
  attributes?: ContentFileAttributes;
};

type Params = {
  webhookId: string;
  params: Record<string, unknown>;
  driveOptions?: DriveOptions;
  notificationConfig?: NotificationConfig;
  /**
   * Present ⇒ run this against the free-quota webhook and declare why.
   * Absent ⇒ the normal paid submit. One field rather than a boolean plus a
   * reason, so a free run can't be sent without saying which case it is.
   */
  freeQuotaReason?: FreeQuotaReason;
};

/**
 * Submits a flow run. `freeQuotaReason` sends it to the free-quota webhook
 * instead — tagged with the reason so the backend meters the cases apart —
 * raising `FreeQuotaExceededError` on 429 so the caller can fall back to the
 * paid endpoint. The server owns the count, we only react to it.
 */
const submitWebHook = async ({
  notificationConfig,
  freeQuotaReason,
  ...params
}: Params): Promise<{ id: string }> => {
  const response = await getContext().utils.api.fetch(
    freeQuotaReason ? FREE_QUOTA_SUBMIT_WEB_HOOK_URL : SUBMIT_WEB_HOOK_URL,
    {
      method: 'POST',
      body: JSON.stringify({
        params,
        ...(notificationConfig && { notification: notificationConfig }),
      }),
      headers: {
        ...getAuthJsonHeaders(),
        ...(freeQuotaReason && {
          [FREE_QUOTA_REASON_HEADER]: freeQuotaReason,
        }),
      },
    }
  );
  if (freeQuotaReason && response.status === QUOTA_EXCEEDED_STATUS) {
    throw new FreeQuotaExceededError();
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Webhook submission failed: ${body || response.statusText}`
    );
  }
  const data: SubmitWebhookData = await response.json();
  if (!data?.response?.id) {
    throw new Error('Webhook response missing id field');
  }
  return { id: data.response.id };
};

export default submitWebHook;
